import React, { useState } from 'react';
import { Form as AntdForm, DatePicker, Input, Select } from 'antd-v5';
import dayjs from 'dayjs';
import ReCAPTCHA from 'react-google-recaptcha';
import EcoImg from 'eco-design/base/eco-img';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import { StepProps } from '../createStateMachine';
import { RemoteInstallFormContext } from '../index';
import css from '../index.module.scss';
import { useLang } from '@/i18n';
import { rpcMap } from '@/constants';
import getLocal from '@/app/utils/i18n';
import request from '@/utils/request';

const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;
const installList = [
    { label: 'Smart Home Panel 2', value: 'Smart Home Panel 2' },
    { label: 'Smart Home Panel 3', value: 'Smart Home Panel 3' },
    { label: 'Smart Gateway', value: 'Smart Gateway' },
];

const Form: React.FC<StepProps<RemoteInstallFormContext>> = ({ send, context }) => {
    const { locale, query } = getLocal();
    const { t } = useLang('questionnaire');

    /** 人机验证 */
    const hl = rpcMap[locale] || rpcMap['us'];

    /** 表单 */
    const [loading, setLoading] = useState(false);
    const [form] = AntdForm.useForm();
    const handleDone = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true); // 开始 loading
            handleSubmit(values);
        } catch (err) {
            console.log('校验失败:', err);
            setLoading(false);
        }
    };
    const handleSubmit = async (params: any) => {
        const newParams = {
            ...params,
            source: query.utm_source ? query.utm_source : undefined,
            installationExecutor: context.step4 === 'my' ? t('step4_item2') : t('step4_item1'),
            installationDate: dayjs(params.installationDate).format('YYYY-MM-DD'),
        };
        const res = await request.post(`${baseUrl(locale)}/remoteInstallForm`, {
            data: newParams,
        });
        setLoading(false); // 结束 loading
        // 邮箱重复
        if (res.code === '5022') {
            form.setFields([
                {
                    name: 'email',
                    errors: [t('form_email_duplicate')],
                },
            ]);
            return;
        }
        if (res.code === '0') {
            send('done');
        }
    };

    return (
        <div className={css.step}>
            <div className="step-head">
                <EcoImg
                    pc="https://websiteoss.ecoflow.com/upload/image/1766458160480/pc_shp2.jpg"
                    h5="https://websiteoss.ecoflow.com/upload/image/1766458160670/m_shp2.jpg"
                />
                <span>{t('step_head')}</span>
            </div>
            <div className="Form-title">{t('form_title')}</div>
            <AntdForm className="Form-content" form={form} disabled={loading}>
                <AntdForm.Item name="fullName" rules={[{ required: true, message: t('form_fullName_required') }]}>
                    <Input placeholder={t('form_fullName')} maxLength={50} />
                </AntdForm.Item>
                <AntdForm.Item name="phone" rules={[{ required: true, message: t('form_phone_required') }]}>
                    <Input placeholder={t('form_phone')} onInput={(e: any) => (e.target.value = e.target.value.replace(/\D/g, ''))} maxLength={20} />
                </AntdForm.Item>
                <AntdForm.Item
                    name="email"
                    rules={[
                        { required: true, message: t('form_email_required') },
                        { type: 'email', message: t('form_email_invalid') },
                    ]}
                >
                    <Input placeholder={t('form_email')} maxLength={100} />
                </AntdForm.Item>
                <AntdForm.Item name="productToInstall" rules={[{ required: true, message: t('form_productToInstall_required') }]}>
                    <Select placeholder={t('form_productToInstall')} options={installList} onChange={(e) => console.log(e)} />
                </AntdForm.Item>
                <AntdForm.Item name="batteriesInfo" rules={[{ required: true, message: t('form_batteriesInfo_required') }]}>
                    <Input placeholder={t('form_batteriesInfo')} maxLength={20} />
                </AntdForm.Item>
                <AntdForm.Item name="installationDate" rules={[{ required: true, message: t('form_installationDate_required') }]}>
                    <DatePicker
                        placeholder={t('form_installationDate')}
                        suffixIcon={
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3.33337" y="5" width="13.3333" height="11.6667" rx="0.833333" stroke="#83868A" strokeWidth="1.25" />
                                <path d="M6.66663 9.375H13.3333" stroke="#83868A" strokeWidth="1.25" strokeLinecap="round" />
                                <path d="M6.66663 3.3335V5.8335" stroke="#83868A" strokeWidth="1.25" strokeLinecap="round" />
                                <path d="M13.3334 3.3335V5.8335" stroke="#83868A" strokeWidth="1.25" strokeLinecap="round" />
                            </svg>
                        }
                        disabledDate={(current) => current && current < dayjs().endOf('day')}
                    />
                </AntdForm.Item>
                <AntdForm.Item name="address" rules={[{ required: true, message: t('form_address_required') }]}>
                    <Input.TextArea placeholder={t('form_address')} maxLength={200} />
                </AntdForm.Item>
                <AntdForm.Item name="purchaseChannel" rules={[{ required: true, message: t('form_purchaseChannel_required') }]}>
                    <Input.TextArea placeholder={t('form_purchaseChannel')} maxLength={100} />
                </AntdForm.Item>
                <AntdForm.Item className="span-2" name="notes" rules={[{ required: false }]}>
                    <Input.TextArea placeholder={t('form_notes')} maxLength={200} />
                </AntdForm.Item>
                <AntdForm.Item name="recaptchaResponse" rules={[{ required: true, message: t('form_recaptchaResponse_required') }]}>
                    {/* @ts-ignore */}
                    <ReCAPTCHA
                        hl={hl}
                        sitekey={SECRET_KEY as string}
                        onChange={(token) => form.setFieldsValue({ recaptchaResponse: token })}
                        onExpired={() => form.setFieldsValue({ recaptchaResponse: undefined })}
                        theme="light"
                    />
                </AntdForm.Item>
            </AntdForm>
            <div className="step-btns">
                <button className={`step-btn step-btn-prev ${loading ? 'step-btn-disabled' : ''}`} onClick={() => (loading ? undefined : send('BACK'))}>
                    {t('btn1')}
                </button>
                <button className={`step-btn step-btn-next ${loading ? 'step-btn-disabled' : ''}`} onClick={loading ? undefined : handleDone}>
                    {t('btn4')}
                </button>
            </div>
        </div>
    );
};
export default Form;
